import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { AssignmentOutlined, Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { examService } from '../../../services/examService';
import { SubjectStatus } from '../../../types';
import { StudentExam, StudentExams } from '../../../types/examination';
import { useAppSelector } from '../../../store/hooks';
import { getGradeColorFromPercentage } from '../../../utils/gradeUtils';
import { fmtClassName } from '../../../utils/pdf';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import ReasonDialog from '../../common/ReasonDialog';
import EmptyState from '../../common/EmptyState';
import PaperHistoryDialog, { HistoryTarget } from '../marks/PaperHistoryDialog';
import ExamResultCard, { ExamAction } from './ExamResultCard';
import EditExamMarksDialog from './EditExamMarksDialog';

/** Which student's year in which class. */
export interface StudentResultsTarget {
  studentId: string;
  studentName: string;
  classId: string;
  sessionId: string;
}

/**
 * A student's exams in a class for the session — every paper, the overall
 * and pass/fail — and, for their class teacher, the corrections: edit a
 * paper, mark a whole exam not appeared, clear an exam, or (with the
 * permission) unlock submitted papers.
 */
const StudentResultsDialog: React.FC<{ target: StudentResultsTarget | null; onClose: () => void; onChanged: () => void }> = ({ target, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const grades = useAppSelector(state => state.grades.grades);
  const [data, setData] = useState<StudentExams | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<StudentExam | null>(null);
  const [confirm, setConfirm] = useState<{ action: 'notAppeared' | 'clear'; exam: StudentExam } | null>(null);
  const [unlocking, setUnlocking] = useState<StudentExam | null>(null);
  const [history, setHistory] = useState<HistoryTarget | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!target) return;
    setError(null);
    try {
      setData(await examService.getStudentExams(target.studentId, target.classId, target.sessionId));
    } catch (e: any) {
      setError(e?.message || 'Could not load the results');
    }
  }, [target]);

  useEffect(() => { setData(null); load(); }, [load]);

  const changed = useCallback(() => { load(); onChanged(); }, [load, onChanged]);
  const gradeColor = useCallback((pct: number | null) => (pct == null ? '#757575' : getGradeColorFromPercentage(pct, grades)), [grades]);

  const onAction = useCallback((action: ExamAction, exam: StudentExam) => {
    if (action === 'edit') setEditing(exam);
    else if (action === 'unlock') setUnlocking(exam);
    else setConfirm({ action, exam });
  }, []);

  const onHistory = useCallback((exam: StudentExam, paper: { subjectId: string; subjectName: string }) =>
    setHistory({ resultId: exam.resultId, subjectId: paper.subjectId, title: `${target?.studentName} · ${paper.subjectName} · ${exam.examName}` }), [target]);

  const runConfirmed = async () => {
    if (!confirm || !target) return;
    const { action, exam } = confirm;
    setBusy(true);
    try {
      const outcome = action === 'clear'
        ? await examService.clearStudentMarks(target.studentId, exam.examId)
        : await examService.recordStudentMarks(target.studentId, exam.examId, exam.papers.filter(p => !p.isLocked).map(p => ({ subjectId: p.subjectId, status: SubjectStatus.NOT_APPEARED })));
      const skipped = outcome.refused.length ? ` ${outcome.refused.length} submitted paper${outcome.refused.length === 1 ? ' was' : 's were'} left as they are.` : '';
      enqueueSnackbar(`${action === 'clear' ? 'Cleared' : 'Marked not appeared'}: ${exam.examName}.${skipped}`, { variant: outcome.refused.length ? 'info' : 'success' });
      setConfirm(null);
      changed();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'That didn\'t go through', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const lockedOf = (exam: StudentExam) => exam.papers.filter(p => p.isLocked);

  return (
    <>
      <Dialog open={!!target} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: { md: 'min(880px, 92vh)' } } }}>
        <DialogTitle component="div" sx={{ pb: 1.5 }}>
          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>{target?.studentName}</Typography>
              {data && (
                <Typography variant="body2" color="text.secondary">
                  {fmtClassName(data.class.name)}{data.section ? ` · ${data.section.name}` : ''} · Roll {data.enrollment.rollNumber ?? '—'}
                  {data.student.grNumber ? ` · GR ${data.student.grNumber}` : ''} · {data.session.name}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close"><Close fontSize="small" /></IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ bgcolor: 'background.default' }}>
          {error ? <Alert severity="error">{error}</Alert>
            : !data ? <LinearProgress />
            : data.exams.length === 0 ? (
              <EmptyState icon={<AssignmentOutlined />} title="No exams yet" description="Exams appear here once they're published for this class." />
            ) : (
              <Stack spacing={2}>
                {data.exams.map(exam => (
                  <ExamResultCard
                    key={exam.resultId}
                    exam={exam}
                    canEdit={data.canEdit}
                    canUnlock={data.canUnlock}
                    gradeColor={gradeColor}
                    onAction={onAction}
                    onHistory={onHistory}
                  />
                ))}
              </Stack>
            )}
        </DialogContent>
      </Dialog>

      <EditExamMarksDialog
        studentId={target?.studentId ?? ''}
        studentName={target?.studentName ?? ''}
        exam={editing}
        onClose={() => setEditing(null)}
        onSaved={changed}
      />

      <ConfirmationDialog
        open={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={runConfirmed}
        loading={busy}
        title={confirm?.action === 'clear' ? `Clear ${confirm.exam.examName}?` : `Not appeared for ${confirm?.exam.examName}?`}
        message={confirm?.action === 'clear'
          ? `Every recorded paper goes back to not entered. Submitted papers stay until they're unlocked. Each paper's history keeps what it was.`
          : `Every open paper is marked not appeared, so this exam is left out of ${target?.studentName}'s overall %. Use it for a student who joined after the exam or was excused from it.`}
        confirmText={confirm?.action === 'clear' ? 'Clear marks' : 'Mark not appeared'}
        confirmColor={confirm?.action === 'clear' ? 'error' : 'primary'}
      />

      <ReasonDialog
        open={!!unlocking}
        onClose={() => setUnlocking(null)}
        title={`Unlock ${unlocking?.examName ?? ''}?`}
        facts={[
          { label: 'Student', value: target?.studentName },
          { label: 'Submitted papers', value: unlocking ? lockedOf(unlocking).map(p => p.subjectName).join(', ') : '' },
        ]}
        consequence="These papers become editable again. Each paper's history keeps who unlocked it and why."
        confirmText="Unlock"
        confirmColor="warning"
        reasonPlaceholder="What needs correcting?"
        onConfirm={async reason => {
          if (!unlocking || !target) return;
          await examService.unlockMarks(unlocking.examId, lockedOf(unlocking).map(p => p.subjectId), { studentIds: [target.studentId] }, reason);
          enqueueSnackbar(`${unlocking.examName} unlocked for ${target.studentName}`, { variant: 'success' });
          changed();
        }}
      />

      <PaperHistoryDialog target={history} onClose={() => setHistory(null)} />
    </>
  );
};

export default StudentResultsDialog;
